
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { setDataType, deletePrefix } from '@/views/editor/composable/useNote.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { updateFormulaPositions } from '@/views/editor/composable/table/useFunction.ts'

export function useAddCell() {
	function addCell(type: string) {
		const { cellTarget, tableSelector, blockList, selector, noteType, noteUid } = storeToRefs(useEditorStore())
		const useCommon = useCommonStore()
		const { socket, lang } = storeToRefs(useCommon)

		if (cellTarget.value.classList.contains('number--line')) {
			return
		}

		const table = cellTarget.value.closest('table')
		if (!table) {
			return
		}

		// tableSelector를 tbody 기반으로 재생성
		useCreateTable().tableShapes(table)

		if (!tableSelector.value || tableSelector.value.length === 0) {
			return
		}

		// 100행/열 제한 체크
		const currentRowCount = tableSelector.value.length
		const currentColCount = tableSelector.value[0]?.length || 0
		
		if ((type === 'up' || type === 'down') && currentRowCount >= 100) {
			useCommon.open('error', lang.value.modal.title, lang.value.table.addMaxError, null)
			return
		}
		
		if ((type === 'left' || type === 'right') && currentColCount >= 100) {
			useCommon.open('error', lang.value.modal.title, lang.value.table.addMaxError, null)
			return
		}

		// 실제 DOM에서 블록 찾기
		let actualBlock = cellTarget.value.closest('.block') as HTMLElement
		
		// .block이 없으면 ID로 블록 찾기 (table 제외)
		if (!actualBlock) {
			let parent = cellTarget.value.parentElement
			let depth = 0
			while (parent && depth < 10) {
				// table 태그는 건너뛰고 계속 올라감
				if (parent.tagName !== 'TABLE' && parent.tagName !== 'TBODY' && parent.tagName !== 'TR' && 
				    parent.id && (parent.id.startsWith('temp-') || parent.id.startsWith('prefix-'))) {
					actualBlock = parent as HTMLElement
					break
				}
				parent = parent.parentElement
				depth++
			}
		}
		
		// actualBlock을 못 찾으면 selector.value.id 사용
		if (!actualBlock) {
			actualBlock = document.getElementById(selector.value.id) as HTMLElement
		}
		
		if (!actualBlock) {
			return
		}

		const processedCellIds: Set<string> = new Set()
		let minCellNumber = -1
		let selectCellNumber = -1
		let maxCellNumber = -1
		let minRowNumber = -1
		let selectRowNumber = -1
		let maxRowNumber = -1
		let targetCellColSpan = 1
		let targetCellRowSpan = 1
		let selectDataColIndex = -1
		let insertedRowIndex = -1

		// 선택된 셀의 범위 계산 (병합 셀의 colspan/rowspan 고려)
	// 선택된 셀의 범위 계산 (병합 셀의 colspan/rowspan 고려)
	// 병합 셀이 여러 행/열에 중복 저장되어 있으므로, 처음 발견된 위치에서만 계산
	const rangeCalculated = new Set<string>()
	tableSelector.value.forEach((rows: any, rowIndex: number) => {
		let dataColIdx = -1
		const processedColIds = new Set<string>()
		rows.forEach((cell: any, cellIndex: number) => {
			// number--line 제외하고 인덱스 계산
			if (!cell.html?.classList?.contains('number--line')) {
				// 병합 셀 중복 체크 - 중복 엔트리는 dataColIdx 증가에서 제외
				const cellColSpan = cell.html.colSpan || 1
				if (cellColSpan > 1 && cell.html?.id && processedColIds.has(cell.html.id)) {
					// 중복 엔트리 - dataColIdx 증가 없이 스킵
				} else {
					if (cellColSpan > 1 && cell.html?.id) {
						processedColIds.add(cell.html.id)
					}
					dataColIdx++
				}
			}
			
			// DOM 요소로 직접 비교
			if (cell.html === cellTarget.value) {
				// 동일한 셀을 이미 처리했는지 확인
				if (!rangeCalculated.has(cell.html.id)) {
					rangeCalculated.add(cell.html.id)
					const cellColSpan = cell.html.colSpan || 1
					
					// 병합 셀의 물리적 끝 위치 계산
					const cellEndCol = cellIndex + cellColSpan
					
					if (selectDataColIndex === -1) {
						selectDataColIndex = dataColIdx
					}

					if (minCellNumber === -1) {
						minCellNumber = dataColIdx - 1
					}

					if (selectCellNumber === -1) {
						if (type === 'right') {
							// right: 병합 셀의 물리적 끝 cellIndex 사용
							selectCellNumber = cellEndCol
						} else if (type === 'left') {
							// left: 병합 셀의 물리적 시작 cellIndex 사용
							selectCellNumber = cellIndex
						}
					}
					if (maxCellNumber < cellEndCol) {
						maxCellNumber = cellEndCol
						targetCellColSpan = cellColSpan
					}

					if (minRowNumber === -1) {
						minRowNumber = rowIndex - 1 < 0 ? 0 : rowIndex - 1
					}

					if (selectRowNumber === -1) {
						selectRowNumber = rowIndex
					}

					// 병합 셀의 rowspan을 고려하여 실제 끝 행 계산
					const cellRowSpan = cell.html.rowSpan || 1
					const cellEndRow = rowIndex + cellRowSpan
					if (maxRowNumber < cellEndRow) {
						maxRowNumber = cellEndRow
						targetCellRowSpan = cellRowSpan
					}
				}
			}
		})
	})

		const rows: any = []

		switch (type) {
			case 'left':
			case 'right':
				tableSelector.value.forEach((rowList: any, rowIndex: number) => {
					const cells = [...rowList]
					
					// 각 행마다 processedCellIds 초기화 (같은 행 내 병합 셀 중복 방지)
					processedCellIds.clear()
					
					// 실제 데이터 셀 인덱스 계산 (number--line 제외)
					let dataColIndex = -1
					let inserted = false
					
					cells.forEach((cell: any, colIndex: number) => {
						// number--line 셀은 건너뛰기
						if (cell.html?.classList?.contains('number--line')) {
							return
						}
						
					// 병합 셀 중복 체크 - 중복 엔트리는 dataColIndex 증가에서 제외
					const currentCellColSpan = cell.html?.colSpan || 1
					const isDuplicate = currentCellColSpan > 1 && cell.html?.id && processedCellIds.has(cell.html.id)
					if (isDuplicate) {
						return
					}
					
					dataColIndex++
					
					// 병합 셀 ID 기록 (첫 번째 엔트리 처리 후 다음 엔트리에서 스킵하도록)
					if (currentCellColSpan > 1 && cell.html?.id) {
						processedCellIds.add(cell.html.id)
					}
					
					// 물리적 colIndex로 비교 (모든 행에서 같은 물리적 위치에 삽입)
					if (colIndex === selectCellNumber) {
						inserted = true
						// 새 셀 생성 (병합 셀이든 일반 셀이든 항상 새 셀 추가)
						const newCell = document.createElement('td')
						
						// 현재 행에 헤더가 있는지 확인
						const dataCells = cells.filter((c: any) => !c.html?.classList?.contains('number--line'))
						let tmpCellHeader: boolean = false
						dataCells.forEach((tmpCell: any) => {
							if (tmpCell.html && tmpCell.html.dataset.header === 'true') {
								tmpCellHeader = true
							}
						})

						if (tmpCellHeader) {
							newCell.id = useCommonStore().uniqueId()
							newCell.dataset.header = 'true'
							const newDiv = document.createElement('div')
							newDiv.setAttribute('contenteditable', 'true')
							newCell.appendChild(newDiv)
						} else {
							newCell.id = useCommonStore().uniqueId()
							const newDiv = document.createElement('div')
							newDiv.setAttribute('contenteditable', 'true')
							newCell.appendChild(newDiv)
						}
						
						const data = {
							html: newCell,
						}

						// selectCellNumber = 물리적 colIndex
						// colIndex가 selectCellNumber와 일치하면, 그 위치에 삽입
						tableSelector.value[rowIndex].splice(colIndex, 0, data)
					}
					})
					
					// 루프에서 삽입하지 못한 경우 (selectCellNumber가 테이블 범위 밖)
					// 행의 끝에 추가
					if (!inserted) {
						const newCell = document.createElement('td')
						
						// 현재 행에 헤더가 있는지 확인
						const dataCells = cells.filter((c: any) => !c.html?.classList?.contains('number--line'))
						let tmpCellHeader: boolean = false
						dataCells.forEach((tmpCell: any) => {
							if (tmpCell.html && tmpCell.html.dataset.header === 'true') {
								tmpCellHeader = true
							}
						})

						if (tmpCellHeader) {
							newCell.id = useCommonStore().uniqueId()
							newCell.dataset.header = 'true'
							const newDiv = document.createElement('div')
							newDiv.setAttribute('contenteditable', 'true')
							newCell.appendChild(newDiv)
						} else {
							newCell.id = useCommonStore().uniqueId()
							const newDiv = document.createElement('div')
							newDiv.setAttribute('contenteditable', 'true')
							newCell.appendChild(newDiv)
						}
						
						const data = {
							html: newCell,
						}
						
						// 행의 끝에 추가
						tableSelector.value[rowIndex].push(data)
					}
				})
				break

			case 'up':
			case 'down':
				let rowDataToInsert: any = null
				let insertIndex: number = -1
				
				for (let rowIndex = 0; rowIndex < tableSelector.value.length; rowIndex++) {
					const rowList = tableSelector.value[rowIndex]
					if (rowIndex === selectRowNumber) {
						const rowData: any = []

						rowList.forEach((cell: any, colIndex: number) => {
							const beforeRowNumber: number = type === 'up' ? minRowNumber : selectRowNumber
							const nextRowNumber: number = type === 'up' ? selectRowNumber : maxRowNumber

							const isBeforeOutOfBounds = beforeRowNumber < 0 || beforeRowNumber >= tableSelector.value.length
							const isNextOutOfBounds = nextRowNumber < 0 || nextRowNumber >= tableSelector.value.length

							const safeBeforeIndex = Math.max(0, Math.min(beforeRowNumber, tableSelector.value.length - 1))
							const safeNextIndex = Math.max(0, Math.min(nextRowNumber, tableSelector.value.length - 1))

							const beforeCell: any = tableSelector.value[safeBeforeIndex][colIndex]?.html
							const selectCell: any = tableSelector.value[safeNextIndex][colIndex]?.html

							if (!beforeCell || !selectCell) {
								return
							}

							const newCell = document.createElement('td')

							// 같은 행을 비교하는 경우는 병합하지 않음 (beforeRowNumber === nextRowNumber)
							const isSameRow = beforeRowNumber === nextRowNumber
							const isMergeCondition = !isSameRow && !isBeforeOutOfBounds && !isNextOutOfBounds && beforeCell === selectCell && !beforeCell.classList.contains('number--line') && !processedCellIds.has(cell.html.id)
							
							if (isMergeCondition) {
								newCell.id = beforeCell.id
								beforeCell.rowSpan += 1
								const newDiv = document.createElement('div')
								newDiv.setAttribute('contenteditable', 'true')
								newCell.appendChild(newDiv)
							} else {
								newCell.id = useCommonStore().uniqueId()
								const newDiv = document.createElement('div')
								newDiv.setAttribute('contenteditable', 'true')
								newCell.appendChild(newDiv)
							}

							const data = {
								html: newCell,
							}

							rowData.push(data)
							processedCellIds.add(cell.html.id)
						})

						rowDataToInsert = rowData
						// 병합 셀의 rowspan을 고려한 삽입 위치 계산
						insertIndex = type === 'up' ? rowIndex : maxRowNumber
						insertedRowIndex = insertIndex
						break
					}
				}

				if (rowDataToInsert && insertIndex >= 0) {
					tableSelector.value.splice(insertIndex, 0, rowDataToInsert)
				}
				break
		}

		if (type === 'left' || type === 'right') {
			const insertedColIndex = type === 'left' ? selectDataColIndex + 1 : selectDataColIndex + 2
			updateFormulaPositions('col', insertedColIndex)
		} else if (insertedRowIndex >= 0) {
			updateFormulaPositions('row', insertedRowIndex + 1)
		}
// 
// 		tableSelector.value.forEach((rowList: any, idx: number) => {
// 		})
// 
// 		// DOM을 수정했으므로 tableSelector를 다시 생성하여 변경사항 반영
// 		useCreateTable().tableShapes(table)
// 
// 		console.log('[useAddCell] After tableShapes - tableSelector:', tableSelector.value.map((row: any, idx: number) => ({
// 			rowIndex: idx,
// 			cells: row.map((cell: any) => ({
// 				id: cell.html?.id,
// 				colSpan: cell.html?.colSpan,
// 				rowSpan: cell.html?.rowSpan,
// 				isNumberLine: cell.html?.classList?.contains('number--line')
// 			}))
// 		})))

		const widthArray: number[] = []
		const heightArray: number[] = []

		tableSelector.value.forEach((rowList: any, rowIndex: number) => {
			if (rowIndex === 0) {
				rowList.forEach((cell: any, cellIndex: number) => {
					if (cell.html) {
						const width = parseInt(cell.html.style.width) || 120
						widthArray.push(width)
					}
				})
			}
		})


	const domRowHeights: number[] = []
	if (table) {
		for (let i = 1; i < table.rows.length; i++) {
			const numberLineCell = table.rows[i].querySelector('td.number--line') as HTMLElement
			const h = numberLineCell?.style.height ? parseInt(numberLineCell.style.height) : 40
			domRowHeights.push(h || 40)
		}
	}

	tableSelector.value.forEach((_rowList: any, rowIndex: number) => {
		let rowHeight = 40
		if (insertedRowIndex >= 0) {
			if (rowIndex === insertedRowIndex) {
				rowHeight = 40
			} else if (rowIndex < insertedRowIndex) {
				rowHeight = domRowHeights[rowIndex] || 40
			} else {
				rowHeight = domRowHeights[rowIndex - 1] || 40
			}
		} else {
			rowHeight = domRowHeights[rowIndex] || 40
		}
		heightArray.push(rowHeight)
	})

		// rows 배열 생성 직전에 processedCellIds 초기화
		processedCellIds.clear()

		tableSelector.value.forEach((rowList: any, rowIndex: number) => {
			const currentRow: any[] = []
			rowList.forEach((cell: any, cellIndex: number) => {
			// number--line 셀은 건너뛰기 (insertTable에서 자동 추가됨)
			if (cell.html && cell.html.classList && cell.html.classList.contains('number--line')) {
				return
			}
			
				if (!cell.html || typeof cell.html === 'string') {
					currentRow.push({
						cellUid: `temp-empty-${rowIndex}-${cellIndex}`,
						colSpan: 1,
						rowSpan: 1,
						content: '',
						dataType: {
							type: 'normal',
							colSpan: 1,
							rowSpan: 1
						}
					})
					return
				}
				
				const isMergedCell = (cell.html.rowSpan > 1) || (cell.html.colSpan > 1)
				if (isMergedCell && processedCellIds.has(cell.html.id)) {
					return
				}
				
				const dataType: any = setDataType(cell.html)
			
			// colSpan과 rowSpan을 dataType에 명시적으로 설정
			dataType.colSpan = cell.html.colSpan || 1
			dataType.rowSpan = cell.html.rowSpan || 1

				if (rows.length === 0 && cellIndex === 0) {
					dataType.size = JSON.stringify({
						width: widthArray,
						height: heightArray
					})
				}

				let content = ''
				if (cell.html.dataset.type === 'function') {
					const spanEl = cell.html.querySelector('span')
					content = spanEl ? spanEl.innerHTML : ''
				} else {
					const divEl = cell.html.querySelector('div')
					content = divEl ? divEl.innerHTML : ''
				}

				const cellId = cell.html.id || ''
				let cellUidValue = ''

				if (cellId.startsWith('prefix-temp-')) {
					cellUidValue = cellId.substring(7)
				} else if (cellId.startsWith('prefix-')) {
					cellUidValue = deletePrefix(cellId)
				} else if (cellId.startsWith('temp-')) {
					cellUidValue = cellId
				} else if (cellId) {
					cellUidValue = 'temp-' + cellId
				}

				currentRow.push({
					cellUid: cellUidValue,
					colSpan: cell.html.colSpan,
					rowSpan: cell.html.rowSpan,
					content: content,
					dataType: dataType,
				})

				if (isMergedCell) {
					processedCellIds.add(cell.html.id)
				}
			})

			rows.push(currentRow)
		})

		// 실제 DOM에서 가져온 블록 ID 사용
		const actualBlockId = actualBlock?.id || selector.value.id
		const isTemp = actualBlockId.includes('temp')
		const blockUid = isTemp ? actualBlockId : deletePrefix(actualBlockId)
		
		// 현재 table의 ID를 보존
		const currentTable = actualBlock?.querySelector('table')
		const currentTableId = currentTable?.id ? currentTable.id.replace('prefix-', '') : ''

		let blockContent: any
		let tableUid: string = ''
		let blockFound = false

		blockList.value.forEach((item: any, index: number) => {
			const itemBlockUid = item.blockUid || item.rawBlockId || ''
			
			if (itemBlockUid === blockUid) {
				blockFound = true
				if (typeof item.content === 'string') {
					try {
						item.content = item.content ? JSON.parse(item.content) : {}
					} catch (e) {
						item.content = {}
					}
				}

				if (!item.content || typeof item.content !== 'object') {
					item.content = {}
				}

				item.content.cell = rows
				if (currentTableId) {
					item.content.tableUID = currentTableId
				}
				blockContent = item.content
				tableUid = item.content.tableUID || item.content.tableUid || ''
			}
		})

		if (!blockFound) {
			
			// DOM에서 직접 테이블 정보 가져오기
			const tableElement = actualBlock.querySelector('table')
			if (tableElement) {
				// table의 ID에서 tableUid 추출
				const tableId = tableElement.id
				if (tableId.startsWith('prefix-temp-')) {
					tableUid = tableId.substring(7) // 'prefix-' 제거
				} else if (tableId.startsWith('prefix-')) {
					tableUid = deletePrefix(tableId)
				} else if (tableId.startsWith('temp-')) {
					tableUid = tableId
				}
				
				blockContent = {
					type: 'defaultTable',
					tableUID: tableUid,
					cell: rows
				}
				
			}
		}


		// actualBlockId로 DOM 업데이트
		const block: any = document.getElementById(actualBlockId) as HTMLElement

		if (block && blockContent) {
			const tableHtml = useCreateTable().insertTable(blockContent, blockUid)
			if (tableHtml) {
				block.innerHTML = tableHtml

				const updatedTable = block.querySelector('table')
				if (updatedTable) {
					useCreateTable().tableShapes(updatedTable)
				}
			}
		}

		if (noteType.value === 'Project' && blockUid) {
			const params = {
				noteUID: noteUid.value,
				blockUID: blockUid,
				new: false,
				content: {
					type: 'defaultTable',
					tableUID: tableUid,
					cell: rows,
				},
			}
			socket.value.createTableBlock(params)
		}

		new UseHistory().push(false, { tableOperation: true })
	}

	return {
		addCell,
	}
}

